/**
 * A voltage-sense terminal used as a bus. A BMV's B+ lead or a custom
 * monitor's 1 A sense post measures a battery; it distributes nothing. Two
 * cables on it means somebody is feeding a load, or bonding two banks,
 * through a 1 A wire — the second is the worse case, because the wire then
 * carries whatever equalising current the two banks disagree by, unfused and
 * unsized. `cable-current.ts` reads a sense terminal as 0 A for the same
 * reason, so the sizing engine cannot be the thing that notices.
 */
import type { DefLookup } from "../catalog";
import { detectMonitorPower } from "./monitor-power";
import { dcPortTerminalGroups } from "../component-ports";
import { computeBatteryStacks } from "../battery-stack";
import { isShuntPassthrough } from "../cable-current";
import { diagnosticLabel } from "../i18n/diagnostic-label";
import { isSenseTerminal } from "../sense-terminal";
import { findNode } from "../system-lookup";
import type { Issue, System, Terminal } from "../types";
import { withoutDanglingCables } from "./cable-endpoints";
import {
  buildGraph,
  findTerminals,
  isBatteryPositive,
  reachable,
  multiSourceBfs,
  pathCables,
  splitV,
  v,
  type Graph,
  type Vertex,
} from "./graph";

const labelOf = (system: System, defLookup: DefLookup, nodeId: string): string => {
  const node = findNode(system, nodeId);
  if (!node) return nodeId;
  if (node.kind === "zone") return node.label ?? diagnosticLabel("diag.label.zone");
  const def = defLookup(node.definitionId, node.customDefinition);
  if (def?.renderVariant === "junction") return diagnosticLabel("diag.label.junction");
  return node.label ?? def?.shortName ?? def?.name ?? diagnosticLabel("diag.label.component");
};

/** Battery-positive vertex → index of the group it shares a rail with. */
function positiveGroups(graph: Graph, battPositives: readonly Vertex[]): Map<Vertex, number> {
  const groupOf = new Map<Vertex, number>();
  let g = 0;
  for (const seed of battPositives) {
    if (groupOf.has(seed)) continue;
    for (const r of reachable(graph, seed)) {
      if (battPositives.includes(r) && !groupOf.has(r)) groupOf.set(r, g);
    }
    g++;
  }
  return groupOf;
}

export function detectSenseTerminalAsBus(
  input: System,
  defLookup: DefLookup,
): Issue[] {
  const system = withoutDanglingCables(input, defLookup);
  const cablesAt = new Map<Vertex, string[]>();
  for (const c of system.cables) {
    for (const vert of [
      v(c.fromNodeId, c.fromTerminalId),
      v(c.toNodeId, c.toTerminalId),
    ]) {
      const list = cablesAt.get(vert);
      if (list) list.push(c.id);
      else cablesAt.set(vert, [c.id]);
    }
  }

  // `voltageSense`: a monitor's B+/AUX lead, where a second cable is a bus
  // use by definition. Other `signal` terminals (an alternator's field, a
  // stereo's ACC, an MPPT's COM) are daisy-chained control wiring and only
  // matter here when they bond two battery positives.
  const senseVerts: Array<{
    vertex: Vertex;
    nodeId: string;
    term: Terminal;
    voltageSense: boolean;
  }> = [];
  for (const n of system.nodes) {
    if (n.kind !== "component") continue;
    const def = defLookup(n.definitionId, n.customDefinition);
    if (!def) continue;
    for (const term of def.terminals) {
      if (!isSenseTerminal(def, term)) continue;
      const vertex = v(n.id, term.id);
      if (!cablesAt.has(vertex)) continue;
      senseVerts.push({
        vertex,
        nodeId: n.id,
        term,
        voltageSense: def.category === "monitor",
      });
    }
  }
  if (senseVerts.length === 0) return [];

  // Which battery positives share a rail with sense wires in place, and which
  // still do with every sense wire lifted: a pair that only meets through a
  // sense terminal is bonded by it.
  const battPositives = findTerminals(system, defLookup, isBatteryPositive);
  const full = buildGraph(system, defLookup);
  const withoutSense =
    battPositives.length >= 2
      ? positiveGroups(buildGraph(system, defLookup, { excludeSense: true }), battPositives)
      : new Map<Vertex, number>();

  const issues: Issue[] = [];
  for (const s of senseVerts) {
    const cableIds = cablesAt.get(s.vertex) ?? [];

    // Batteries this lead reaches that are not on one rail without it.
    const bonded: string[] = [];
    if (battPositives.length >= 2) {
      // Everything reachable from the lead shares one rail with it; the
      // question is whether those batteries still share one without it.
      const reach = reachable(full, s.vertex);
      const seenGroups = new Set<number>();
      const hits: Vertex[] = [];
      for (const bp of battPositives) {
        if (!reach.has(bp)) continue;
        hits.push(bp);
        seenGroups.add(withoutSense.get(bp) ?? -1);
      }
      if (seenGroups.size >= 2) {
        for (const bp of hits) {
          const { nodeId } = splitV(bp);
          if (!bonded.includes(nodeId)) bonded.push(nodeId);
        }
      }
    }

    const fannedOut = s.voltageSense && cableIds.length >= 2;
    if (!fannedOut && bonded.length === 0) continue;

    const label = labelOf(system, defLookup, s.nodeId);
    const vars = {
      label,
      terminal: s.term.label,
      count: cableIds.length,
      maxAmp: s.term.maxAmp,
      batteries: bonded.map((id) => labelOf(system, defLookup, id)).join(" / "),
    };
    issues.push({
      code: "sense-terminal-as-bus",
      severity: "critical",
      title: { key: "diag.sense-terminal-as-bus.title", vars },
      message: {
        key:
          bonded.length > 0
            ? "diag.sense-terminal-as-bus.message_bonds"
            : "diag.sense-terminal-as-bus.message",
        vars,
      },
      nodeIds: [s.nodeId, ...bonded],
      cableIds,
      fix: { key: "diag.sense-terminal-as-bus.fix", vars },
    });
  }
  return issues;
}

export function runSenseDiagnostics(system: System, defLookup: DefLookup): Issue[] {
  return [...detectSenseTerminalAsBus(system, defLookup), ...detectShuntBypass(system, defLookup), ...detectMonitorPower(system, defLookup)];
}

/** A battery monitor cannot count a return path which avoids its shunt.
 * Scope equipment to the monitored positive bank, so a common negative does
 * not make an unrelated starter bank's loads appear to bypass a house shunt.
 * This describes whole-bank monitoring. The schematic has no DC-meter mode
 * setting; intentional branch-meter installations need that context assessed
 * separately, which is why the suggested correction is explicitly conditional.
 * Victron SmartShunt installation §3.4:
 * https://www.victronenergy.com/media/pg/SmartShunt/en/installation.html */
export function detectShuntBypass(input: System, defLookup: DefLookup): Issue[] {
  const system = withoutDanglingCables(input, defLookup);
  const full = buildGraph(system, defLookup);
  const power = buildGraph(system, defLookup, { excludeSense: true });
  const stacks = computeBatteryStacks(system, defLookup).stacks;
  const issues: Issue[] = [];
  for (const shunt of system.nodes) {
    if (shunt.kind !== "component") continue;
    const def = defLookup(shunt.definitionId, shunt.customDefinition);
    if (!def || (!def.currentShunt && !isShuntPassthrough(def))) continue;
    const batteryTerm = def.terminals.find((t) => t.polarity === "negative" &&
      (def.currentShunt ? t.id === def.currentShunt.batteryTerminalId : t.direction === "input"));
    const systemTerm = def.terminals.find((t) => t.polarity === "negative" &&
      (def.currentShunt ? t.id === def.currentShunt.systemTerminalId : t.direction === "output"));
    if (!batteryTerm || !systemTerm || batteryTerm.id === systemTerm.id) continue;
    const input = v(shunt.id, batteryTerm.id), output = v(shunt.id, systemTerm.id);
    // Only remove this shunt's measuring element, keeping any real bypass.
    const cut: Graph = { ...power, adj: new Map([...power.adj].map(([vertex, edges]) => [vertex, edges.filter(({ via }) =>
      !(via.kind === "intra" && via.link.nodeId === shunt.id &&
        ((via.link.from === batteryTerm.id && via.link.to === systemTerm.id) ||
         (via.link.from === systemTerm.id && via.link.to === batteryTerm.id))),
    )])) };
    const batterySide = reachable(cut, input);
    let candidates = stacks.filter((bank) => bank.bottomNegatives.some((neg) => batterySide.has(neg)));
    // The main B+ sense identifies the measured bank; AUX is deliberately
    // excluded. Lynx's battery-positive input provides the same evidence.
    const reference = def.terminals.find((t) => def.currentShunt?.referenceTerminalId
      ? t.id === def.currentShunt.referenceTerminalId
      : t.id === "vbat" || t.id === "batt-pos");
    if (reference) {
      const positiveSense = reachable(full, v(shunt.id, reference.id));
      const identified = candidates.filter((bank) => bank.topPositives.some((pos) => positiveSense.has(pos)));
      if (identified.length > 0) candidates = identified;
    }
    // Multiple unmatched banks on one return are ambiguous; don't guess.
    if (candidates.length !== 1) continue;
    const bank = candidates[0];
    const positiveRail = new Set(bank.topPositives.flatMap((pos) => [...reachable(power, pos)]));
    const { prev } = multiSourceBfs(cut, bank.bottomNegatives);
    const offenders = new Set<string>();
    const cables = new Set<string>();
    for (const node of system.nodes) {
      if (node.kind !== "component" || node.id === shunt.id) continue;
      const equipment = defLookup(node.definitionId, node.customDefinition);
      if (!equipment || !["load", "charger", "source", "engine"].includes(equipment.category)) continue;
      for (const side of ["input", "output", "bidi"] as const) {
        for (const terminals of dcPortTerminalGroups(equipment, side)) {
          const positives = terminals.filter((t) => t.polarity === "positive");
          if (!positives.some((t) => positiveRail.has(v(node.id, t.id)))) continue;
          for (const terminal of terminals.filter((t) => t.polarity === "negative" || t.polarity === "ground")) {
            const endpoint = v(node.id, terminal.id);
            if (!batterySide.has(endpoint) || !prev.has(endpoint)) continue;
            const path = pathCables(cut, endpoint, prev);
            if (path.length === 0) continue;
            offenders.add(node.id);
            for (const cableId of path) cables.add(cableId);
          }
        }
      }
    }
    if (offenders.size === 0) continue;
    // A completely unwired shunt is not evidence of an intended monitor.
    if (!(power.adj.get(output)?.some((edge) => edge.via.kind === "cable"))) continue;
    const vars = { label: labelOf(system, defLookup, shunt.id), equipment: [...offenders].map((id) => labelOf(system, defLookup, id)).join(", "), terminal: systemTerm.label };
    issues.push({
      code: "shunt-bypass", severity: "warning",
      title: { key: "diag.shunt-bypass.title", vars },
      message: { key: "diag.shunt-bypass.message", vars },
      fix: { key: "diag.shunt-bypass.fix", vars },
      nodeIds: [shunt.id, ...offenders], cableIds: [...cables],
    });
  }
  return issues;
}
